import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FollowUpConfigFormAttributes from 'pages/FollowUpConfig/FollowUpConfigFormAttributes';
import { useMemo, useState } from 'react';
import { useGetFollowUpCampaignsQuery } from 'services/api/followUpConfig.api';
import type { FollowUpConfig } from 'services/types/followUpConfig';
import { NAVY, RADIUS_COIN, SHADOW, YELLOW } from 'styles/tokens';

import { interactiveCardSx, labelSx, sectionGap, sectionSubtitleSx, sectionTitleSx } from '../styles';

interface SelectFollowUpProps {
  businessUnitId: number;
  selectFollowUp: (followUpId: FollowUpConfig) => void;
}

const FollowUpCard = ({
  followUp,
  businessUnitId,
  isSelected,
  onSelect,
}: {
  followUp: FollowUpConfig;
  businessUnitId: number;
  isSelected: boolean;
  onSelect: (followUp: FollowUpConfig) => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Grid size={{ xs: 12, sm: 6 }} key={followUp.id} sx={{ alignSelf: 'flex-start' }}>
      <Box
        onClick={() => onSelect(followUp)}
        sx={{
          ...interactiveCardSx,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          ...(isSelected && {
            boxShadow: `0 0 0 2px ${YELLOW}, ${SHADOW}`,
            backgroundColor: alpha(YELLOW, 0.06),
            '&:hover': {
              backgroundColor: alpha(YELLOW, 0.06),
              boxShadow: `0 0 0 2px ${YELLOW}, 0 8px 24px rgba(21, 24, 58, 0.12)`,
            },
          }),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={labelSx}>Identificador</Typography>
            <Typography sx={{ ...sectionTitleSx, mt: 0.5 }}>{followUp.identifier}</Typography>
            {followUp.templateName && (
              <Typography sx={{ ...sectionSubtitleSx, mt: 0.25 }}>Plantilla: {followUp.templateName}</Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            sx={{
              color: alpha(NAVY, 0.45),
              transition: 'transform 0.2s ease',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ pt: 2, borderTop: `1px solid ${alpha(NAVY, 0.06)}` }}>
            <FollowUpConfigFormAttributes
              followUpConfigDraft={{
                ...followUp,
                conditionsToSend: JSON.stringify(followUp.conditionsToSend),
                templateComponents: JSON.stringify(followUp.templateComponents),
              }}
              isEditing={false}
              businessUnitId={businessUnitId}
            />
          </Box>
        </Collapse>
      </Box>
    </Grid>
  );
};

const SelectFollowUp = ({ businessUnitId, selectFollowUp }: SelectFollowUpProps) => {
  const { data: surveyFollowUps = [], isLoading } = useGetFollowUpCampaignsQuery({
    businessUnitId,
    isActive: true,
    hasSurvey: true,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<FollowUpConfig | null>(null);

  const filteredFollowUps = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return surveyFollowUps;
    return surveyFollowUps.filter((followUp) => {
      const haystack = [followUp.identifier, followUp.templateName, followUp.surveyConfig?.surveyName]
        .filter((v): v is string => !!v)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [surveyFollowUps, searchTerm]);

  const handleCardClick = (followUp: FollowUpConfig) => {
    setSelected((prev) => (prev?.id === followUp.id ? null : followUp));
  };

  return (
    <Box>
      <Box sx={{ mb: sectionGap }}>
        <Typography sx={sectionTitleSx}>Selecciona una encuesta</Typography>
        <Typography sx={sectionSubtitleSx}>Selecciona la encuesta que quieres enviar y confirma tu elección</Typography>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && surveyFollowUps.length > 0 && (
        <Box sx={{ mb: sectionGap, maxWidth: 420 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por identificador, plantilla o encuesta"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: alpha(NAVY, 0.55) }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      )}

      {!isLoading && !surveyFollowUps.length && (
        <Alert severity="info" sx={{ mb: sectionGap, fontFamily: 'Jost, sans-serif' }}>
          No hay encuestas existentes en esta unidad de negocio. Crea una nueva desde la pestaña "Nueva encuesta".
        </Alert>
      )}

      {!isLoading && surveyFollowUps.length > 0 && filteredFollowUps.length === 0 && (
        <Alert severity="info" sx={{ mb: sectionGap, fontFamily: 'Jost, sans-serif' }}>
          No hay encuestas que coincidan con tu búsqueda.
        </Alert>
      )}

      <Grid container spacing={3}>
        {filteredFollowUps.map((followUp) => (
          <FollowUpCard
            key={followUp.id}
            followUp={followUp}
            businessUnitId={businessUnitId}
            isSelected={selected?.id === followUp.id}
            onSelect={handleCardClick}
          />
        ))}
      </Grid>

      {selected && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: sectionGap }}>
          <Button
            variant="contained"
            onClick={() => selectFollowUp(selected)}
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: YELLOW,
              color: NAVY,
              borderRadius: RADIUS_COIN,
              px: 4,
              py: 1.2,
              '&:hover': { backgroundColor: alpha(YELLOW, 0.85) },
            }}
          >
            Confirmar encuesta
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SelectFollowUp;
